import TokenBlot from "@/common/quill/TokenBlot";

const TOKEN_MATCHER = /\{\{(\w*)\}\}/gi;

// Block-level HTML elements used by unwrapStructuralDivs to decide whether a
// <div>'s children are all block-level (and thus the div is a pure structural
// wrapper). Kept at module scope so the Set isn't reallocated on every call.
const BLOCK_TAGS = new Set([
  "DIV", "P", "UL", "OL", "LI", "DL", "DT", "DD",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "PRE", "TABLE", "THEAD", "TBODY", "TR", "TD", "TH",
  "HR", "SECTION", "ARTICLE", "HEADER", "FOOTER", "NAV", "ASIDE",
  "FIGURE", "MAIN", "ADDRESS",
]);

class Formatter {

  // Adds wrapping <p> around paragraphs in a way that doesn't break display of current HTML
  // It is essential for Quill to render newlines correctly. This is equivalent to Ruby StringUtils.tranform_for_quill
  //
  // More context: when Quill adds the <p> tags to regular old HTML (this is what older campaigns use)
  // it does so in a way that causes irregular line breaks:
  //     double newlines after <br><br>
  //     newline before and after token values
  // This function will wrapping tags ourselves and do so
  static transformForQuill(text) {
    if (!text) return;

    // remove whitespace between ul and li tags. quill will show bulleted list as blank otherwise
    text = text.replace(/<ul>\s<li>/g, "<ul><li>");
    text = text.replace(/<\/li>\s<\/ul>/g, "</li></ul>");
    text = text.replace(/<\/li>\s<li>/g, "</li><li>");

    // remove <p> tags nested inside <li> tags. Quill treats both as Block blots and
    // can't nest blocks, so <li><p>text</p></li> causes the list content to be dropped.
    // ATS job descriptions commonly produce this nesting.
    // Note: non-greedy match means nested lists (<li>...<ul><li>...</li></ul></li>)
    // match from outer <li> to the first </li>, which still produces correct output.
    text = text.replace(/(<li\b[^>]*>)([\s\S]*?)<\/li>/gi, (match, openTag, inner) => {
      if (!/<p\b/i.test(inner)) return match;
      inner = inner.replace(/<\/p>\s*<p\b[^>]*>/gi, "<br>");
      inner = inner.replace(/<p\b[^>]*>/gi, "");
      inner = inner.replace(/<\/p>/gi, "");
      return `${openTag}${inner}</li>`;
    });

    // Split <p>...<br>...</p> into sibling <p> blocks. Quill drops inline <br>s inside
    // a block on round-trip, which would otherwise smash <br>-separated content together.
    // Only splits on <br>s that are direct children of the <p> — nested <br>s inside
    // inline wrappers (<span>, <strong>, ...) are left alone to avoid unbalancing the
    // wrapper; those fall back to Quill's default drop-br behavior.
    text = Formatter.splitParagraphsOnTopLevelBr(text);

    // break if text has already been transformed. This transformed value would have come from DB
    if (text.startsWith("<p>")) {
      return text;
    }

    let transformed = text.replace(/(.*?)<br>/g, (match, p1) => `<p>${p1.trim()}</p>`);
    const lastPTagIndex = transformed.lastIndexOf("</p>");

    if (lastPTagIndex === -1) {
      transformed = `<p>${transformed.trim()}</p>`;
    } else if (lastPTagIndex < transformed.length - 4) {
      transformed = `${transformed.substring(0, lastPTagIndex + 4)}<p>${transformed.substring(lastPTagIndex + 4).trim()}</p>`;
    }

    return transformed;
  }

  static splitParagraphsOnTopLevelBr(text) {
    if (!text || !text.includes("<br") || !text.includes("<p")) return text;

    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");

    Array.from(doc.body.querySelectorAll("p")).forEach(p => {
      const kids = Array.from(p.childNodes);
      if (!kids.some(n => n.nodeName === "BR")) return;
      // Skip Quill's blank-line pattern (<p><br></p>) — splitting it would double blank
      // lines on every save/load cycle.
      const isBlank = n => n.nodeName === "BR" || (n.nodeType === Node.TEXT_NODE && !n.textContent.trim());
      if (kids.every(isBlank)) return;

      const groups = [[]];
      kids.forEach(n => {
        if (n.nodeName === "BR") groups.push([]);
        else groups[groups.length - 1].push(n);
      });

      const frag = doc.createDocumentFragment();
      groups.forEach(group => {
        const newP = doc.createElement("p");
        for (const attr of p.attributes) newP.setAttribute(attr.name, attr.value);
        const groupIsBlank = group.length === 0 || group.every(n => n.nodeType === Node.TEXT_NODE && !n.textContent.trim());
        if (groupIsBlank) {
          newP.appendChild(doc.createElement("br"));
        } else {
          group.forEach(n => newP.appendChild(n));
        }
        frag.appendChild(newP);
      });

      p.parentNode.replaceChild(frag, p);
    });

    return doc.body.innerHTML;
  }

  // Flatten any <div> holding block-level content (>= 1 block child). Such a
  // div adds no semantic value, and the caller's div→p rewrite would nest a <p>
  // around its block children — which browsers auto-close into empty paragraphs.
  // Block children are lifted in place; inline runs around them are wrapped in
  // their own <p> (so no bare text reaches transformForQuill). Purely-inline and
  // empty/whitespace divs are left for the div→p rewrite, which preserves their
  // attributes. Attributes on a flattened block-wrapper div are dropped — a class
  // on a pure block container can't survive Quill's flat model anyway, and
  // keeping it is what produced the nested-<p> blank lines.
  static unwrapStructuralDivs(text) {
    // Case-insensitive precheck: the div→p rewrite is also case-insensitive, so
    // uppercase wrappers (e.g. "<DIV>") must not slip past this fast path.
    if (!text || !/<div/i.test(text)) return text;

    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");

    const isWhitespaceText = n => n.nodeType === Node.TEXT_NODE && !n.textContent.trim();
    const isBlockElement = n => n.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(n.nodeName);

    // Outer divs are visited first; an inner div lifted out of an unwrapped
    // parent stays in the snapshot with a live parent and is processed later.
    let mutated = false;
    Array.from(doc.body.querySelectorAll("div")).forEach(div => {
      if (!div.parentNode) return;

      const meaningful = Array.from(div.childNodes).filter(n => !isWhitespaceText(n));
      if (meaningful.length === 0) return;            // empty/whitespace → leave for div→p
      if (!meaningful.some(isBlockElement)) return;   // purely inline → leave for div→p

      const parent = div.parentNode;
      let inlineRun = [];
      const flushInlineRun = () => {
        if (inlineRun.length === 0) return;
        if (inlineRun.every(isWhitespaceText)) {
          inlineRun.forEach(n => parent.insertBefore(n, div)); // keep spacing between blocks
        } else {
          const p = doc.createElement("p");
          inlineRun.forEach(n => p.appendChild(n));
          parent.insertBefore(p, div);
        }
        inlineRun = [];
      };
      while (div.firstChild) {
        const child = div.firstChild;
        if (isBlockElement(child)) {
          flushInlineRun();
          parent.insertBefore(child, div); // moves child out of div
        } else {
          div.removeChild(child);
          inlineRun.push(child);
        }
      }
      flushInlineRun();
      parent.removeChild(div);
      mutated = true;
    });

    // Nothing unwrapped → return the original to avoid DOM round-trip
    // normalization (quote styles, <br/> → <br>) on inputs with no such divs.
    if (!mutated) return text;

    // Preserve elements DOMParser hoisted into <head> (<title>, <meta>, <base>,
    // <link>) instead of dropping them; original inline position isn't recoverable.
    const headHtml = doc.head ? doc.head.innerHTML : "";
    return headHtml + doc.body.innerHTML;
  }

  static stripWrappingTags(text) {
    // Remove the opening and closing P tags that Quill imposes on every line
    return text.replace(/<p[^>]*>/gi, "").replace(/<\/p>/gi, "");
  }

  // Replace the token embeds from the Quill output with placeholder syntax.
  static htmlToMustacheTokens(htmlString, allowHtml) {
    const parser = new DOMParser();
    const dom = parser.parseFromString(htmlString, "text/html");
    dom.querySelectorAll(`.${TokenBlot.className}`).forEach(token => {
      const { dataset: { slug } } = token;
      token.replaceWith(`{{${slug}}}`);
    });

    return allowHtml ? dom.body.innerHTML : dom.body.innerText;
  }

  static mustacheToHtmlTokens(tokenizedString, svc, disabled) {
    if (!tokenizedString) {
      return;
    }
    return tokenizedString.replace(TOKEN_MATCHER, (match, slug) => {
      const allowedVariables = svc.values();
      // Ignore matches that aren't for a real token.
      if (!allowedVariables.includes(slug)) {
        return;
      }
      const variable = svc.variableFor(slug);
      const name = variable.name;

      return `<${TokenBlot.tagName} draggable="${!disabled}" class="${TokenBlot.className} ${disabled ? "disabled" : ""} ${variable.value.replace(/_/g, "-")}" contenteditable="false" data-title="${name}" data-slug="${variable.value}">${name}</${TokenBlot.tagName}>`;
    });
  }

  static autocorrectIssues(content) {
    if (!content) {
      return;
    }

    // ATS sources often wrap content in deeply nested <div>s that carry no
    // semantic meaning — they're pure structural grouping. Unwrapping them
    // before the div→p rewrite avoids producing a stack of empty <p></p>
    // siblings (which Quill renders as <p><br></p> blank lines).
    content = Formatter.unwrapStructuralDivs(content);

    // https://github.com/davidroyer/vue2-editor/issues/216
    // These divs should only be legacy campaigns created w/ Froala
    // Use DOM parsing to properly handle nested elements (e.g., signatures with links)
    content = content.replace(/<div/gi, "<p");
    content = content.replace(/<\/div>/gi, "</p>");
    content = content.replace(/<font/gi, "<p");
    content = content.replace(/<\/font>/gi, "</p>");

    content = content.replace(/&nbsp;?/gm, " ");

    // requires a space, HTML tag, or start of line before tokens
    content = content.replace(/([^\s>]){{/gm, "$1 {{");

    // requires a space, HTML tag, or word boundary after tokens
    content = content.replace(/}}(\b|{[^\s<])/gm, "}} $1");

    content = content.replace(/}}\s+/gm, "}} ")
    content = content.replace(/}} ([.,!])/gm, "}}$1")
    content = content.replace(/\s+{{/gm, " {{")
    return content;
  }
}

export default Formatter;
